import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUser, FaEnvelope, FaPhone, FaHandsHelping, FaCheckCircle, 
  FaBriefcase, FaCalendarAlt, FaVenusMars, FaMapMarkerAlt, FaHome 
} from 'react-icons/fa';

const BecomeVolunteer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    occupation: '',
    age: '',
    gender: '',
    city: '',
    address: '',
    interest: 'Community Service',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Volunteer Data:", formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ 
        name: '', email: '', phone: '', occupation: '', 
        age: '', gender: '', city: '', address: '', 
        interest: 'Community Service', message: '' 
      });
    }, 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      
      {/* 1. HERO SECTION */}
      <section 
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/Iftaar Dinner(1).jpeg')` }}
      >
        <div className="text-center text-white">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            className="text-5xl md:text-6xl font-serif font-black uppercase tracking-tight"
          >
            Join Our Team
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>
          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Become Volunteer</span>
          </p>
        </div>
      </section>

      {/* 2. MAIN CONTENT SECTION */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          
          {/* Left Side: Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8 sticky top-24"
          >
            <div>
              <h4 className="text-[#f9b50b] font-black uppercase tracking-[0.3em] text-xs mb-3">Make a Difference</h4>
              <h2 className="text-5xl font-serif font-bold text-[#221f1f] leading-tight uppercase">
                Serve Your <br />
                <span className="text-[#f9b50b]">Community</span>
              </h2>
              <div className="h-1.5 w-24 bg-[#f9b50b] mt-6"></div>
            </div>

            <p className="text-gray-500 text-lg leading-relaxed italic">
              "The best way to find yourself is to lose yourself in the service of others." 
              Join the Illinois Interfaith Organization and help us build bridges of peace and understanding.
            </p>

            <div className="space-y-6">
              {[
                "Support local interfaith events",
                "Help in community outreach programs",
                "Contribute to social justice advocacy",
                "Build meaningful cross-cultural connections"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 text-[#221f1f] font-bold uppercase text-sm">
                  <FaCheckCircle className="text-[#f9b50b] text-xl" /> {item}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Side: Form Container */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-[#221f1f] p-8 md:p-12 rounded-3xl shadow-2xl relative overflow-hidden"
          >
            <FaHandsHelping className="absolute -right-10 -bottom-10 text-white opacity-5 text-9xl pointer-events-none" />

            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                <motion.form 
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit} 
                  className="space-y-5 relative z-10"
                >
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Full Name *</label>
                    <div className="relative">
                      <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                      <input 
                        type="text" name="name" required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  {/* Email & Phone Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Email Address *</label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                        <input 
                          type="email" name="email" required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                          placeholder="Email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Phone Number *</label>
                      <div className="relative">
                        <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                        <input 
                          type="tel" name="phone" required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                          placeholder="Phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Occupation & Age Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Occupation</label>
                      <div className="relative">
                        <FaBriefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                        <input 
                          type="text" name="occupation"
                          value={formData.occupation}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                          placeholder="Professional / Student"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Age</label>
                      <div className="relative">
                        <FaCalendarAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                        <input 
                          type="number" name="age"
                          value={formData.age}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                          placeholder="Years"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Gender & City Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Gender</label>
                      <div className="relative">
                        <FaVenusMars className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                        <select 
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm appearance-none cursor-pointer"
                        >
                          <option value="" className="text-black">Select Gender</option>
                          <option value="male" className="text-black">Male</option>
                          <option value="female" className="text-black">Female</option>
                          <option value="other" className="text-black">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">City</label>
                      <div className="relative">
                        <FaMapMarkerAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-[#f9b50b]" />
                        <input 
                          type="text" name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                          placeholder="Your City"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <label className="text-white text-[10px] font-black uppercase tracking-widest ml-1">Full Address</label>
                    <div className="relative">
                      <FaHome className="absolute left-4 top-4 text-[#f9b50b]" />
                      <textarea 
                        name="address" rows="2"
                        value={formData.address}
                        onChange={handleChange}
                        className="w-full bg-white/10 border border-white/20 p-4 pl-12 text-white rounded-xl focus:border-[#f9b50b] focus:outline-none transition-all text-sm placeholder:text-gray-500"
                        placeholder="Street, State, Zip Code"
                      ></textarea>
                    </div>
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-[#f9b50b] text-[#221f1f] font-black uppercase py-4 rounded-xl hover:bg-white transition-all shadow-lg shadow-[#f9b50b]/10 mt-4 active:scale-95"
                  >
                    Send Application
                  </button>
                </motion.form>
              ) : (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-20"
                >
                  <div className="w-20 h-20 bg-[#f9b50b] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#f9b50b]/40">
                    <FaCheckCircle className="text-[#221f1f] text-4xl" />
                  </div>
                  <h3 className="text-white text-3xl font-serif font-bold uppercase mb-4">Application Sent!</h3>
                  <p className="text-gray-400">Thank you for your interest. <br/> Our team will contact you shortly.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BecomeVolunteer;